import React, { useState, useEffect } from 'react';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import TypeAheadLookup from '../../../components/TypeAheadLookup';
import { BudgetChartModal } from '../modals';
import { ChevronDown, X, Save, Trash2, BarChart2 } from 'lucide-react';

const UNITS = ['EACH','BOX','CASE','KG','LB','LITRE','METRE','PACK','PAIR','SET','DOZEN','ROLL','SHEET','BAG','BOTTLE','CARTON'];

export default function LineItemDetailEditor({ item, requestNumber, onSave, onDelete, onCancel, isNew, readOnly, mandatoryLineFields = [], hideSupplier = false }) {
  const { labels: expLabels } = useExpenditureLabels();
  const { fmtInput, fmtTotal } = useDecimalFormat();
  const [form, setForm] = useState(() => ({
    ...item,
    quantity: fmtInput(item.quantity),
    unit_price: fmtInput(item.unit_price),
    discount: fmtInput(item.discount),
    sales_tax: fmtInput(item.sales_tax),
  }));
  const [menuOpen, setMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [estimatingCO2, setEstimatingCO2] = useState(false);
  const [budgetModal, setBudgetModal] = useState(null);
  const f = (k, v) => { if (!readOnly) setForm(p => ({ ...p, [k]: v })); };

  const estimateCO2 = async () => {
    if (!form.item_description) return;
    setEstimatingCO2(true);
    try {
      const r = await api.post('/api/ai/carbon-estimate', { description: form.item_description, quantity: parseFloat(form.quantity) || 1 });
      f('co2e', r.data.co2e_kg || 0);
      toast.success(`CO2e estimated: ${r.data.co2e_kg} kg (${r.data.confidence} confidence)`);
    } catch (e) { toast.error(getApiErrorMessage(e, 'CO2 estimation failed')); }
    setEstimatingCO2(false);
  };

  const isM = (field) => mandatoryLineFields.includes(field);
  const mLabel = (field, label) => (
    <Label className={`text-[11px] font-semibold ${isM(field) ? 'text-red-500' : 'text-muted-foreground'}`}>{label}{isM(field) ? ' *' : ''}</Label>
  );

  const expCodeLabel = (field, label, code, seg) => (
    <div className="flex items-center gap-1">
      <Label className={`text-[11px] font-semibold ${isM(field) ? 'text-red-500' : 'text-primary'}`}>{label}{isM(field) ? ' *' : ''}</Label>
      {code && <button onClick={() => setBudgetModal({ code, label, segment: seg })} className="hover:opacity-70" title="View budget chart">
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><rect x="3" y="12" width="4" height="9" rx="1" fill="#2563eb"/><rect x="10" y="7" width="4" height="14" rx="1" fill="#16a34a"/><rect x="17" y="3" width="4" height="18" rx="1" fill="#d97706"/></svg>
      </button>}
    </div>
  );

  const validateAndSave = async () => {
    if (!onSave) return;
    if (!form.item_description?.trim()) { toast.error('Item Description is required'); return; }
    const missing = [];
    const fieldMap = { due_date: 'Item Due Date', supplier: 'Supplier', gl: expLabels.exp_code_4 || 'GL', property_code: expLabels.exp_code_5 || 'Property Code', nominal_code: expLabels.exp_code_6 || 'Nominal Code' };
    for (const field of mandatoryLineFields) {
      if (field === 'due_date' && !form.item_due_date) missing.push(fieldMap[field] || field);
      if (field === 'supplier' && !form.supplier_code) missing.push(fieldMap[field] || field);
      if (field === 'gl' && !form.account_code_4) missing.push(fieldMap[field] || field);
      if (field === 'property_code' && !form.account_code_5) missing.push(fieldMap[field] || field);
      if (field === 'nominal_code' && !form.account_code_6) missing.push(fieldMap[field] || field);
    }
    if (missing.length > 0) { toast.error(`Missing: ${missing.join(', ')}`); return; }
    setSaving(true);
    try {
      await onSave({ ...form, quantity: parseFloat(form.quantity)||0, unit_price: parseFloat(form.unit_price)||0, discount: parseFloat(form.discount)||0, sales_tax: parseFloat(form.sales_tax)||0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Save failed');
    }
    setSaving(false);
  };

  const qty = parseFloat(form.quantity) || 0;
  const price = parseFloat(form.unit_price) || 0;
  const disc = parseFloat(form.discount) || 0;
  const tax = parseFloat(form.sales_tax) || 0;
  const netValue = Math.round(qty * price * (1 - disc / 100) * 100) / 100;
  const grossValue = Math.round(netValue * (1 + tax / 100) * 100) / 100;

  const inputCls = `h-8 text-sm ${readOnly ? 'bg-muted/50' : ''}`;

  return (
    <div className="border-t border-b bg-muted/5 p-4">
      {/* Title */}
      <div className="flex items-center justify-between mb-3">
        <div className="relative">
          <button onClick={() => !isNew && !readOnly && setMenuOpen(!menuOpen)} className="flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
            <ChevronDown className="h-3.5 w-3.5" />
            {isNew ? `Add New Item for Requisition ${requestNumber}` : readOnly ? `Item ${item.item_num} — ${requestNumber}` : `Edit Item ${item.item_num} — ${requestNumber}`}
          </button>
          {menuOpen && (
            <div className="absolute left-0 top-full mt-1 bg-card border rounded-lg shadow-lg z-50 py-1 w-44">
              <button className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent" onClick={async () => {
                setMenuOpen(false);
                try {
                  // Generate item code: ITM + timestamp
                  const itemCode = `ITM-${Date.now().toString(36).toUpperCase()}`;
                  const itemData = {
                    item_code: itemCode,
                    item_description: form.item_description || '',
                    suppliers_part_number: form.suppliers_part_number || '',
                    supplier_code: form.supplier_code || '',
                    supplier_name: form.supplier_name || '',
                    unit_price: parseFloat(form.unit_price) || 0,
                    pack_size: parseInt(form.pack_size) || 1,
                    unit: form.unit || 'EACH',
                    account_code_4: form.account_code_4 || '',
                    account_code_5: form.account_code_5 || '',
                    account_code_6: form.account_code_6 || '',
                    category_code: form.category_code || '',
                    item_type: 'Consumable',
                    active: true,
                  };
                  await api.post('/api/data/items', itemData);
                  toast.success(`Item ${itemCode} added to catalog: ${form.item_description}`);
                } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to add to catalog')); }
              }}>Add to Catalog</button>
              <button className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent" onClick={() => { toast.info('Item copied'); setMenuOpen(false); }}>Copy Item</button>
            </div>
          )}
        </div>
        {readOnly && <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 font-semibold">VIEW ONLY</span>}
      </div>

      {/* 3-column grid: Left (Supplier + Description) | Center (Numbers stacked) | Right (Expense Codes) */}
      <div className="grid grid-cols-[1fr_auto_minmax(280px,1fr)] gap-x-5 gap-y-2.5">

        {/* Row 1: Supplier | Part Number | GL */}
        {!hideSupplier && (
          <div className="space-y-1">
            {mLabel('supplier', 'Suggested Supplier')}
            {readOnly ? <p className="text-sm h-8 flex items-center">{form.supplier_code || '—'}</p> :
              <TypeAheadLookup endpoint="/api/data/suppliers" valueField="supplier_code" labelField="supplier_name" value={form.supplier_code||''} displayValue={form.supplier_code||''} placeholder="Search suppliers..." onChange={(code, record) => { f('supplier_code', code); f('supplier_name', record?.supplier_name || ''); }} />}
            {form.supplier_name && <p className="text-[11px] text-muted-foreground bg-muted/30 rounded px-2 py-1 truncate">{form.supplier_name}</p>}
          </div>
        )}
        {hideSupplier && <div />}
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">Suppliers Part Number</Label>
          <Input value={form.suppliers_part_number||''} onChange={e => f('suppliers_part_number', e.target.value)} className={`${inputCls} w-44`} readOnly={readOnly} />
        </div>
        <div className="space-y-1">
          {expCodeLabel('gl', expLabels.exp_code_4 || 'GL', form.account_code_4, 4)}
          {readOnly ? <p className="text-sm h-8 flex items-center">{form.account_code_4 || '—'}</p> :
            <TypeAheadLookup endpoint="/api/account-codes/4" valueField="code" labelField="description" value={form.account_code_4||''} displayValue={form.account_code_4||''} placeholder={`Search ${expLabels.exp_code_4}...`} onChange={code => f('account_code_4', code)} />}
        </div>

        {/* Row 2+: Description (tall) | Numbers stacked vertically | Expense Codes */}
        <div className="space-y-1 row-span-3">
          <Label className="text-[11px] text-red-500 font-semibold">Item Description *</Label>
          <Textarea value={form.item_description||''} onChange={e => f('item_description', e.target.value)} rows={8} className={`text-sm ${readOnly ? 'bg-muted/50' : ''}`} readOnly={readOnly} />
        </div>

        {/* Center column: numbers stacked vertically */}
        <div className="row-span-3 space-y-2">
          <div className="grid grid-cols-3 gap-1.5">
            <div className="space-y-0.5">
              <Label className="text-[11px] text-red-500 font-semibold">Quantity *</Label>
              <Input type="number" step="0.01" value={form.quantity} onChange={e => f('quantity', e.target.value)} onBlur={() => f('quantity', fmtInput(form.quantity))} className={`${inputCls} text-right`} readOnly={readOnly} />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[11px] text-muted-foreground">Unit</Label>
              {readOnly ? <p className="text-sm h-8 flex items-center">{form.unit || 'EACH'}</p> :
                <select value={form.unit||'EACH'} onChange={e => f('unit', e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-1 text-sm">
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>}
            </div>
            <div className="space-y-0.5">
              <Label className="text-[11px] text-red-500 font-semibold">Pack</Label>
              <Input type="number" value={form.pack_size||1} onChange={e => f('pack_size', parseInt(e.target.value)||1)} className={`${inputCls} text-right`} readOnly={readOnly} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <div className="space-y-0.5">
              <Label className="text-[11px] text-red-500 font-semibold">Price</Label>
              <Input type="number" step="0.01" value={form.unit_price} onChange={e => f('unit_price', e.target.value)} onBlur={() => f('unit_price', fmtInput(form.unit_price))} className={`${inputCls} text-right`} readOnly={readOnly} />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[11px] text-red-500 font-semibold">Disc %</Label>
              <Input type="number" step="0.01" value={form.discount} onChange={e => f('discount', e.target.value)} onBlur={() => f('discount', fmtInput(form.discount))} className={`${inputCls} text-right`} readOnly={readOnly} />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[11px] text-red-500 font-semibold">Tax %</Label>
              <Input type="number" step="0.01" value={form.sales_tax} onChange={e => f('sales_tax', e.target.value)} onBlur={() => f('sales_tax', fmtInput(form.sales_tax))} className={`${inputCls} text-right`} readOnly={readOnly} />
            </div>
          </div>
          <div className="space-y-0.5">
            {mLabel('due_date', 'Item Due Date')}
            <div className="flex gap-1">
              <Input type="date" value={form.item_due_date||''} onChange={e => f('item_due_date', e.target.value)} className={`${inputCls} w-36`} readOnly={readOnly} />
              {!readOnly && <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => f('item_due_date','')}><X className="h-3 w-3" /></Button>}
            </div>
          </div>
        </div>

        {/* Right column: expense codes stacked */}
        <div className="row-span-3 space-y-2">
          <div className="space-y-1">
            {expCodeLabel('property_code', expLabels.exp_code_5 || 'Property Code', form.account_code_5, 5)}
            {readOnly ? <p className="text-sm h-8 flex items-center">{form.account_code_5 || '—'}</p> :
              <TypeAheadLookup endpoint="/api/account-codes/5" valueField="code" labelField="description" value={form.account_code_5||''} displayValue={form.account_code_5||''} placeholder={`Search ${expLabels.exp_code_5}...`} onChange={code => f('account_code_5', code)} />}
          </div>
          <div className="space-y-1">
            {expCodeLabel('nominal_code', expLabels.exp_code_6 || 'Nominal Code', form.account_code_6, 6)}
            {readOnly ? <p className="text-sm h-8 flex items-center">{form.account_code_6 || '—'}</p> :
              <TypeAheadLookup endpoint="/api/account-codes/6" valueField="code" labelField="description" value={form.account_code_6||''} displayValue={form.account_code_6||''} placeholder={`Search ${expLabels.exp_code_6}...`} onChange={code => f('account_code_6', code)} />}
          </div>
          <div className="flex gap-3 items-end">
            <div className="space-y-1 flex-1">
              <Label className="text-[11px] text-muted-foreground">Expense Type</Label>
              {readOnly ? <p className="text-sm h-8 flex items-center">{form.expense_type || 'None'}</p> :
                <select value={form.expense_type||'None'} onChange={e => f('expense_type', e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                  <option>None</option><option>Operating</option><option>Capital</option>
                </select>}
            </div>
            <div className="space-y-1 w-24">
              <div className="flex items-center gap-1">
                <Label className="text-[11px] text-muted-foreground">CO2e (kg)</Label>
                {!readOnly && form.item_description && (
                  <button onClick={estimateCO2} disabled={estimatingCO2} className="text-[9px] text-primary hover:underline font-semibold">
                    {estimatingCO2 ? '...' : 'Estimate'}
                  </button>
                )}
              </div>
              <Input type="number" step="0.01" value={form.co2e||0} onChange={e => f('co2e', parseFloat(e.target.value)||0)} className={`${inputCls} text-right`} readOnly={readOnly} />
            </div>
          </div>
        </div>
      </div>

      {/* Footer: Actions on left, Values on right */}
      <div className="flex items-center gap-4 mt-4 pt-3 border-t">
        {!readOnly && onSave && (
          <Button size="sm" onClick={validateAndSave} disabled={saving}>
            <Save className="h-3 w-3 mr-1" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
        {!readOnly && onDelete && !isNew && <Button size="sm" variant="destructive" onClick={onDelete}><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>}
        <Button size="sm" variant="outline" onClick={onCancel}><X className="h-3 w-3 mr-1" /> {readOnly ? 'Close' : 'Cancel'}</Button>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Net Value:</span>
          <span className="text-sm font-bold text-primary">{fmtTotal(netValue)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Gross Value (incl. tax):</span>
          <span className="text-sm font-bold">{fmtTotal(grossValue)}</span>
        </div>
      </div>

      {/* Budget Chart Modal (same as header) */}
      {budgetModal && (
        <BudgetChartModal open={!!budgetModal} onClose={() => setBudgetModal(null)} code={budgetModal.code} label={budgetModal.label} segment={budgetModal.segment} />
      )}
    </div>
  );
}
